import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const icons = [FiIcons.FiUnlock, FiIcons.FiZap, FiIcons.FiServer, FiIcons.FiHeart];

const ProblemSolution = () => {
  const { language } = useLanguage();
  const text = t[language];

  return (
    <section id="problem" className="py-16 sm:py-24 bg-[#0a0a0f] relative border-t border-white/5 overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px]" />

      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="space-y-6"
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs sm:text-sm font-bold uppercase"
            >
              {text.problem.badge}
            </motion.div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              {text.problem.title}
            </h2>

            <div className="space-y-4 text-gray-400 text-base sm:text-lg leading-relaxed">
              <p>{text.problem.p1}</p>
              <p>{text.problem.p2}</p>
              <p className="text-white font-bold">{text.problem.p3}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
            className="bg-gradient-to-br from-[#1a1a24] to-[#0f0f13] p-6 sm:p-8 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />

            <h3 className="text-xl sm:text-2xl font-bold text-white mb-8 relative z-10">
              {text.benefits.title}
            </h3>

            <div className="space-y-6 relative z-10">
              {text.benefits.items.map((item, idx) => (
                <motion.div
                  key={idx}
                  className="flex gap-4 group"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 + 0.3 }}
                >
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-purple-500/20 flex items-center justify-center shrink-0 border border-orange-500/10 group-hover:bg-orange-500/30 transition-colors"
                  >
                    <SafeIcon icon={icons[idx]} className="text-orange-400 text-xl" />
                  </motion.div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolution;